/**
 * EventPady+ — Pending Approval Page
 *
 * Shown after successful MoMo payment, while the organisation awaits
 * admin review and approval.
 *
 * Also used as the in-dashboard gate for PENDING_PAYMENT, PENDING_REVIEW,
 * SUSPENDED, and REJECTED organisations (rendered by DashboardLayout's gate).
 *
 * URL: /pending-approval
 * Public — no authentication required.
 */
import { Link } from 'react-router-dom'
import { Clock, CheckCircle2, Mail, ShieldOff, XCircle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/constants/routes'
import { APP_NAME } from '@/constants'
import type { OrgStatus } from '@/types'

const SUPPORT_EMAIL = 'support@staidorf.com'

// ---------------------------------------------------------------------------
// Status configurations
// ---------------------------------------------------------------------------

interface StatusConfig {
  icon:    React.ElementType
  iconCls: string
  title:   string
  body:    string
  note?:   string
  /** Whether to render the icon with a spinner overlay (indicates in-progress) */
  spinning?: boolean
}

const STATUS_CONFIG: Record<OrgStatus, StatusConfig> = {
  PENDING_PAYMENT: {
    icon:     Clock,
    iconCls:  'text-blue-600 bg-blue-100',
    spinning: true,
    title:    'Awaiting payment confirmation',
    body:     'We sent a USSD prompt to your mobile wallet. Please approve it on your phone — this page will update automatically once your payment is confirmed.',
    note:     'If you approved the prompt but are still seeing this screen after a few minutes, contact our support team.',
  },
  PENDING_REVIEW: {
    icon:    CheckCircle2,
    iconCls: 'text-emerald-600 bg-emerald-100',
    title:   'Application under review',
    body:    'Your payment was confirmed and your application has been submitted for review. Our team typically approves accounts within 1–2 business days.',
    note:    'You will receive an email notification at the address you registered with once your account is approved.',
  },
  ACTIVE: {
    icon:    CheckCircle2,
    iconCls: 'text-emerald-600 bg-emerald-100',
    title:   'Account active',
    body:    'Your account is active. You should have been redirected to the dashboard.',
    note:    undefined,
  },
  SUSPENDED: {
    icon:    ShieldOff,
    iconCls: 'text-yellow-600 bg-yellow-100',
    title:   'Account suspended',
    body:    'Your account has been temporarily suspended by our team. This may be due to a policy concern or an outstanding issue.',
    note:    'Please contact support to resolve this and restore access.',
  },
  REJECTED: {
    icon:    XCircle,
    iconCls: 'text-destructive bg-destructive/10',
    title:   'Application not approved',
    body:    'We were unable to approve your EventPady+ application at this time.',
    note:    'If you believe this is an error, contact our support team for clarification.',
  },
}

// ---------------------------------------------------------------------------
// Status panel — reusable for both standalone page and DashboardLayout gate
// ---------------------------------------------------------------------------

interface StatusPanelProps {
  status: OrgStatus
  /** Organisation name, if available */
  orgName?: string
  /** Callback fired when the user clicks "Sign out" (gate mode only) */
  onLogout?: () => void
}

export function OrgStatusPanel({ status, orgName, onLogout }: StatusPanelProps) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.PENDING_REVIEW
  const Icon = cfg.icon

  return (
    <div className="flex flex-col items-center text-center max-w-sm mx-auto space-y-6">
      {/* Icon — with optional spinner ring for in-progress states */}
      <div className="relative flex h-20 w-20 items-center justify-center">
        <div className={`flex h-20 w-20 items-center justify-center rounded-full ${cfg.iconCls}`}>
          <Icon className="h-10 w-10" />
        </div>
        {cfg.spinning && (
          <Loader2 className="absolute inset-0 h-20 w-20 animate-spin text-blue-400 opacity-40" />
        )}
      </div>

      {/* Text */}
      <div className="space-y-2">
        <h2 className="text-xl font-bold tracking-tight">{cfg.title}</h2>
        {orgName && (
          <p className="text-sm font-medium text-muted-foreground">{orgName}</p>
        )}
        <p className="text-sm text-muted-foreground leading-relaxed">{cfg.body}</p>
        {cfg.note && (
          <p className="text-xs text-muted-foreground italic">{cfg.note}</p>
        )}
      </div>

      {/* Actions — status-specific */}
      <div className="w-full space-y-3">

        {/* PENDING_PAYMENT: contact support + auto-refresh note */}
        {status === 'PENDING_PAYMENT' && (
          <>
            <div className="flex items-center justify-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-xs text-blue-800">
              <Clock className="h-3.5 w-3.5 shrink-0" />
              Checking payment status every 15 seconds…
            </div>
            <Button variant="outline" asChild className="w-full">
              <a href={`mailto:${SUPPORT_EMAIL}?subject=Payment%20not%20confirmed`}>
                <Mail className="mr-2 h-4 w-4" />
                Contact support
              </a>
            </Button>
          </>
        )}

        {/* PENDING_REVIEW: inbox reminder */}
        {status === 'PENDING_REVIEW' && (
          <div className="flex items-center justify-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            <Mail className="h-4 w-4 shrink-0" />
            Watch your inbox for our approval email.
          </div>
        )}

        {/* SUSPENDED / REJECTED: support link */}
        {(status === 'SUSPENDED' || status === 'REJECTED') && (
          <Button variant="outline" asChild className="w-full">
            <a href={`mailto:${SUPPORT_EMAIL}`}>
              <Mail className="mr-2 h-4 w-4" />
              Email support
            </a>
          </Button>
        )}

        {/* Sign-out / switch account */}
        {onLogout ? (
          <Button variant="ghost" size="sm" onClick={onLogout} className="w-full text-muted-foreground">
            Sign out
          </Button>
        ) : (
          <Button variant="ghost" size="sm" asChild className="w-full text-muted-foreground">
            <Link to={ROUTES.AUTH.LOGIN}>Sign in to a different account</Link>
          </Button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Standalone page (public route — user not yet logged in)
// ---------------------------------------------------------------------------

export default function PendingApprovalPage() {
  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <header className="border-b bg-white px-6 py-4">
        <span className="text-lg font-semibold">{APP_NAME}</span>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-xl border bg-white p-10 shadow-sm">
          <OrgStatusPanel status="PENDING_REVIEW" />
        </div>
      </main>
    </div>
  )
}
